import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, Video, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

export default function Confirmation() {
  const { id } = useParams();
  const [bk, setBk] = useState(null);
  const [wa, setWa] = useState("");
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`).then(r => setBk(r.data)).catch(() => setErr(true));
    api.get("/settings").then(r => setWa(r.data.whatsapp_number));
  }, [id]);

  if (err) return <Layout><p className="text-center py-24 text-slate-600" data-testid="booking-not-found">We couldn't find this booking.</p></Layout>;
  if (!bk) return <Layout><div className="flex justify-center py-24"><Loader2 className="animate-spin text-teal-700" /></div></Layout>;

  const tz = bk.patient_timezone || "Asia/Kolkata";
  const when = new Date(bk.slot_start_utc).toLocaleString("en-IN", { timeZone: tz, dateStyle: "full", timeStyle: "short" });
  const ref = bk.id.slice(0, 8).toUpperCase();
  const msg = `Hello Dr. Porwal, I am ${bk.patient_name} (Booking ID: ${bk.id.slice(0, 8)}). Sharing my medical reports for my consultation on ${bk.ist_date} ${bk.ist_time} IST.`;
  const waLink = `https://wa.me/${wa.replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;

  if (!["confirmed", "completed"].includes(bk.status)) {
    return (
      <Layout>
        <section className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="booking-inactive">
          <h1 className="text-3xl font-serif text-slate-900">This booking is {bk.status.replace("_", " ")}</h1>
          <p className="mt-3 text-slate-600">Reference {ref}. If you believe this is a mistake, please contact us on WhatsApp at {wa}.</p>
          <Link to="/book"><Button className="mt-8 bg-teal-700 hover:bg-teal-800 text-white rounded-full px-6" data-testid="rebook-btn">Book a new consultation</Button></Link>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-14 sm:py-20 reveal">
        <div className="flex items-center gap-3 text-emerald-700"><CheckCircle2 className="w-8 h-8" /><span className="text-sm font-semibold uppercase tracking-wider">Booking confirmed</span></div>
        <h1 className="mt-4 text-3xl sm:text-4xl font-serif text-slate-900" data-testid="confirmation-title">Thank you, {bk.patient_name}. Your consultation is booked.</h1>
        <p className="mt-3 text-slate-600">A confirmation has been sent to <span className="font-medium text-slate-900">{bk.patient_email}</span>.</p>

        <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-3 text-sm" data-testid="confirmation-details">
          <Row k="Reference" v={<span className="font-mono" data-testid="booking-ref">{ref}</span>} />
          <Row k="When" v={`${when} (${tz})`} />
          <Row k="IST" v={`${bk.ist_date} · ${bk.ist_time}`} />
          <Row k="Type" v={bk.consultation_type === "new" ? "New patient" : "Follow-up"} />
          <Row k="Paid" v={`₹${bk.amount_inr.toLocaleString("en-IN")} · ${bk.razorpay_payment_id || "—"}`} />
          <Row k="Status" v={<span className="capitalize" data-testid="booking-status">{bk.status.replace("_", " ")}</span>} />
        </div>

        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 sm:p-8" data-testid="whatsapp-reports-card">
          <h2 className="text-xl font-serif text-slate-900">Please send your reports on WhatsApp</h2>
          <p className="mt-2 text-slate-700 text-sm leading-relaxed">Share all prior reports, scans and prescriptions <strong>24–72 hours before</strong> your appointment so Dr. Porwal can review them in advance.</p>
          <p className="mt-3 text-lg font-semibold text-slate-900" data-testid="whatsapp-number">{wa}</p>
          <a href={waLink} target="_blank" rel="noreferrer"><Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6" data-testid="whatsapp-send-reports-btn"><MessageCircle className="w-4 h-4 mr-2" />Send reports via WhatsApp</Button></a>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 flex gap-4">
          <Video className="w-6 h-6 text-teal-700 shrink-0" />
          <div className="text-sm text-slate-700">
            {bk.meeting_link ? <>Your video link: <a href={bk.meeting_link} className="text-teal-700 underline break-all" data-testid="meeting-link">{bk.meeting_link}</a></> :
              "Your secure video consultation link will be emailed to you before the appointment."}
          </div>
        </div>
        <Link to="/" className="inline-block mt-10 text-teal-700 hover:underline text-sm" data-testid="back-home-link">← Back to home</Link>
      </section>
    </Layout>
  );
}

const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-4"><span className="text-slate-500">{k}</span><span className="col-span-2 text-slate-900">{v}</span></div>
);
